import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NavIcon, type NavIconName } from "../nav/NavIcon";
import { CARD_LAYOUT_MQ, useCardLayout } from "../../utils/useCardLayout";

export interface HomeActionItem {
  to: string;
  icon: NavIconName;
  label: string;
}

/**
 * G-204 (owner 2026-11-04): the home page's shortcuts live behind one floating
 * "Actions" button in the bottom-right corner — the Inventory page's pattern
 * (its `.inv-fab*` rules) — and ONLY while the sidebar is collapsed (the
 * 900 px nav breakpoint). On a desk the sidebar is open beside the page, so
 * the same links there would be the sidebar twice (the owner's own complaint
 * about the old "Quick actions" grid).
 */
export function HomeActions({ items }: { items: HomeActionItem[] }) {
  const collapsedNav = useCardLayout(CARD_LAYOUT_MQ);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!collapsedNav || items.length === 0) return null;
  return (
    <div className="inv-fab">
      {open ? <div className="inv-fab-scrim" aria-hidden="true" onClick={() => setOpen(false)} /> : null}
      {open ? (
        <div className="inv-fab-menu" id="home-actions-menu" role="menu">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              className="inv-fab-item"
              onClick={() => setOpen(false)}
            >
              <NavIcon name={item.icon} size={18} />
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="inv-fab-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? "home-actions-menu" : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <NavIcon name={open ? "close" : "more"} size={18} />
        Actions
      </button>
    </div>
  );
}
